import Vue, { ComponentOptions, CreateElement, VNode, VueConstructor } from "vue";
import { EntityListSearchComponent } from "~shared/builders/entity-list-search.builder";
import { EntityListPaginationComponent } from "~shared/builders/entity-list-pagination.builder";
import { EntityListView } from "~shared/classes/entity-list-view";
import { EntityListProvider } from "~shared/classes/entity-list.provider";
import { DEFAULT_PAGINATION_LIMIT } from "~shared/shared.constants";

type ListViewType = typeof EntityListView;

type ProviderType = typeof EntityListProvider;

interface ExtendedEntityListProvider extends EntityListProvider {
  selectable: boolean;
  visibleHeaders: string[];
}

export class EntityListBuilder {
  private _searchComponent: ComponentOptions<EntityListSearchComponent> | null = null;

  private _paginationComponent: ComponentOptions<EntityListPaginationComponent> | null = null;

  private _listView: ListViewType | null = null;

  private _provider: ProviderType | null = null;

  private buildPagination(h: CreateElement, component: EntityListProvider): VNode | null {
    if (!this._paginationComponent) {
      return null;
    }

    return h(this._paginationComponent, {
      on: {
        onPageChanged(newPage: number) {
          component.setPage(newPage);
        },
      },
      attrs: {
        page: component.page,
        total: Math.ceil(component.total / DEFAULT_PAGINATION_LIMIT),
      },
    });
  }

  private buildSearch(h: CreateElement, component: EntityListProvider): VNode | null {
    if (!this._searchComponent) {
      return null;
    }

    return h(this._searchComponent, {
      on: {
        onSearchByChanged(value: string) {
          component.setSearchBy(value);
        },
        onSearchValueChanged(value: string) {
          component.setSearchValue(value);
        },
      },
    });
  }

  private buildFilters(h: CreateElement, component: EntityListProvider): VNode | null {
    if (!this._searchComponent && !this._paginationComponent) {
      return null;
    }

    const filters: VNode[] = [];

    const pagination = this.buildPagination(h, component);
    const search = this.buildSearch(h, component);

    if (search) {
      filters.push(search);
    }

    if (pagination) {
      filters.push(pagination);
    }

    return h(
      "div",
      {
        staticClass: "entity-list-filters",
      },
      filters
    );
  }

  private buildListView(h: CreateElement, component: ExtendedEntityListProvider): VNode {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return h(this._listView!, {
      attrs: {
        selectable: component.selectable,
        visibleHeaders: component.visibleHeaders,
        items: component.items,
      },
    });
  }

  public withProvider(provider: ProviderType): EntityListBuilder {
    this._provider = provider;
    return this;
  }

  public withPagination(paginationComponent: ComponentOptions<EntityListPaginationComponent>): EntityListBuilder {
    this._paginationComponent = paginationComponent;
    return this;
  }

  public withSearch(searchComponent: ComponentOptions<EntityListSearchComponent>): EntityListBuilder {
    this._searchComponent = searchComponent;
    return this;
  }

  public withListView(listView: ListViewType): EntityListBuilder {
    this._listView = listView;
    return this;
  }

  public build(): VueConstructor<ExtendedEntityListProvider> {
    if (!this._provider) {
      throw new Error("Provider is required to build an EntityList");
    }

    const $ = this;

    return Vue.extend(this._provider).extend({
      props: {
        selectable: {
          default: false,
          type: Boolean,
        },
        visibleHeaders: {
          default: () => [],
        },
      },
      render(h: CreateElement): VNode {
        return h("div", {}, [$.buildFilters(h, this), $.buildListView(h, this)]);
      },
    });
  }
}
